import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnColumn, type BnRow, type BnBadgeVariant } from "../components/index.js";
import { humanError, parseError } from "../api/errors.js";
import {
  getSettings,
  updateLimits,
  pinImage,
  listContainers,
  killContainer,
  runSweep,
  listOverQuotaRepositories,
  listReservedSecrets,
  type OverQuotaRepository,
  type ReservedSecret,
  type AdminSettings,
  type BackupStatus,
  type GlobalLimits,
  type ContainersReport,
  type ContainerInfo,
  type PullStatus,
} from "../api/admin.js";
import {
  adminTabs,
  adminTabsStyles,
  mibToBytes,
  bytesToMib,
  minutesToSeconds,
  secondsToMinutes,
} from "./admin-shared.js";

/** The containers endpoint is polled at 5s; a pin pull is watched at 3s. */
const POLL_MS = 5000;
const PULL_POLL_MS = 3000;

/** relTime renders an ISO timestamp as a coarse "Ns/Nm/Nh ago". */
function relTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** imageTag extracts the tag (or truncated digest) of an image ref for a compact cell. */
function imageTag(ref: string): string {
  const at = ref.lastIndexOf("@");
  // "sha256:" + 12 hex chars is the familiar short-digest form.
  if (at !== -1) return ref.slice(at + 1, at + 20) + "…";
  const colon = ref.lastIndexOf(":");
  return colon > ref.lastIndexOf("/") ? ref.slice(colon + 1) : ref;
}

function pullVariant(status: PullStatus): BnBadgeVariant {
  switch (status) {
    case "pulling":
      return "info";
    case "ready":
      return "success";
    case "error":
      return "error";
    default:
      return "neutral";
  }
}

type BackupState = "off" | "failing" | "ok" | "no run yet";

/**
 * backupState reads the backup badge from the status. A failure at the same
 * time as the last success counts as failing: that run wrote its file but a
 * later stage (copy, prune, sweep) failed. Failing wins over off, because
 * the 35-day prune and sweep still run while database backups are off.
 */
function backupState(b: BackupStatus): BackupState {
  const okAt = b.lastSuccessAt ? Date.parse(b.lastSuccessAt) : Number.NaN;
  const failAt = b.lastFailureAt ? Date.parse(b.lastFailureAt) : Number.NaN;
  if (!Number.isNaN(failAt) && (Number.isNaN(okAt) || failAt >= okAt)) return "failing";
  if (b.schedule === "") return "off";
  return Number.isNaN(okAt) ? "no run yet" : "ok";
}

function backupVariant(state: BackupState): BnBadgeVariant {
  switch (state) {
    case "failing":
      return "error";
    case "ok":
      return "success";
    default:
      return "neutral";
  }
}

/** substrateUnavailable detects the container routes' 503 substrate_unavailable. */
function substrateUnavailable(e: unknown): boolean {
  const { status, code } = parseError(e);
  return status === 503 || code === "substrate_unavailable";
}

/**
 * page-admin ("Runtime", route /admin, operators only) is the container-runtime
 * control panel. One polled endpoint (GET /v1/admin/containers, 5s, paused
 * while the tab is hidden, request-id-guarded like page-usage) feeds the KPI
 * tiles, the sweep stats, and the live containers table; the image pin,
 * database backup and global limits cards read GET /v1/admin/settings once
 * and re-fetch after each mutation. The backup card is read-only; its
 * schedule comes from BINO_CLOUD_BACKUP_CRON. Pinning a new tag answers 202
 * and flips into a 3s fast-poll until the async pull leaves "pulling".
 * Limits are edited in MiB/minutes and converted to the wire's bytes/seconds
 * via admin-shared. When no runtime answers them, the container endpoints
 * answer 503 substrate_unavailable. That renders as an inline notice, not an
 * error page, so settings stay editable.
 */
@customElement("page-admin")
export class PageAdmin extends LitElement {
  @state() private settings?: AdminSettings;
  @state() private report?: ContainersReport;
  @state() private loading = true;
  @state() private error = "";
  @state() private substrateDown = false;
  @state() private containersError = "";

  // Image pin form.
  @state() private tagInput = "";
  @state() private pinBusy = false;

  // Global limits form (display units: cores, MiB, count, minutes).
  @state() private cpuStr = "";
  @state() private memStr = "";
  @state() private pidsStr = "";
  @state() private idleStr = "";
  @state() private wallStr = "";
  @state() private maxStr = "";
  @state() private liveFloorStr = "";
  @state() private liveOnStr = "";
  @state() private branchesStr = "";
  @state() private storageStr = "";
  @state() private saveBusy = false;

  // Repositories above their size limit; empty without repositories.
  @state() private overQuota: OverQuotaRepository[] = [];
  @state() private overQuotaError = "";
  @state() private reserved: ReservedSecret[] = [];
  @state() private reservedError = "";

  // Kill + sweep actions.
  @state() private killTarget: ContainerInfo | null = null;
  @state() private killing = false;
  @state() private sweepBusy = false;

  private pollTimer?: number;
  private pullTimer?: number;
  private containersReq = 0;
  private settingsReq = 0;

  static override styles = [
    adminTabsStyles,
    css`
      :host {
        display: block;
      }
      .head {
        margin-bottom: var(--bino-space-lg);
      }
      h1 {
        margin: 0;
        font-size: var(--bino-font-size-xl);
        font-weight: 600;
        color: var(--bino-text);
      }
      .sections {
        display: flex;
        flex-direction: column;
        gap: var(--bino-space-lg);
      }
      /* KPI tiles: same recipe as page-usage's .tile. */
      .kpis {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: var(--bino-space-md);
      }
      .tile {
        border: 1px solid var(--bino-border);
        border-radius: var(--bino-radius-lg);
        background: var(--bino-surface);
        box-shadow: var(--bino-shadow-header);
        padding: var(--bino-space-lg);
      }
      .tile .eyebrow {
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-sm);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--bino-text-secondary);
      }
      .tile .num {
        margin-top: var(--bino-space-sm);
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-xl);
        font-weight: 600;
        line-height: 1.1;
        color: var(--bino-text);
      }
      .sweep-row {
        display: flex;
        align-items: center;
        gap: var(--bino-space-md);
        margin-top: var(--bino-space-sm);
      }
      .sweep-caption {
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-sm);
        color: var(--bino-text-secondary);
      }
      .sweep-errors p {
        margin: 0 0 var(--bino-space-xs);
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-sm);
        color: var(--bino-error);
        word-break: break-word;
      }
      /* Image pin card. */
      .image-meta {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
        gap: var(--bino-space-md);
        margin-bottom: var(--bino-space-lg);
      }
      .image-meta .k {
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-sm);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--bino-text-secondary);
        margin-bottom: var(--bino-space-xs);
      }
      .image-meta .v {
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-md);
        color: var(--bino-text);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .pin-row {
        display: flex;
        align-items: flex-start;
        gap: var(--bino-space-sm);
      }
      .pin-row bn-input {
        flex: 1;
        min-width: 0;
      }
      .pin-error {
        margin: var(--bino-space-sm) 0 0;
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-sm);
        color: var(--bino-error);
        word-break: break-word;
      }
      .version-error {
        margin: 0 0 var(--bino-space-md);
      }
      .backup-meta {
        margin-bottom: 0;
      }
      /* Limits form: native number inputs in the shared control recipe. */
      .limits-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0 var(--bino-space-md);
      }
      input[type="number"] {
        width: 100%;
        box-sizing: border-box;
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-md);
        color: var(--bino-text);
        padding: 0.5rem 0.6875rem;
        border: 1px solid var(--bino-border-light);
        border-radius: var(--bino-radius);
        background: var(--bino-surface-subtle);
      }
      input[type="number"]:focus {
        outline: none;
        border-color: var(--bino-primary);
        box-shadow: 0 0 0 3px var(--bino-focus-ring);
      }
      .save-row {
        display: flex;
        justify-content: flex-end;
      }
      .notice {
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-base);
        color: var(--bino-text-secondary);
        display: flex;
        align-items: center;
        gap: var(--bino-space-md);
        flex-wrap: wrap;
      }
      .confirm-name {
        font-family: var(--bino-font-mono);
        color: var(--bino-text);
      }
      @media (max-width: 900px) {
        .kpis {
          grid-template-columns: repeat(2, 1fr);
        }
        .limits-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ];

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("visibilitychange", this.onVisibility);
    void this.init();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopPoll();
    this.stopPullPoll();
  }

  /** Pause both polls while the tab is hidden; catch up immediately on return. */
  private onVisibility = () => {
    if (document.hidden) {
      this.stopPoll();
      this.stopPullPoll();
      return;
    }
    if (this.settings && !this.substrateDown && !this.containersError) {
      void this.refreshContainers();
      this.startPoll();
    }
    if (this.settings?.image.pullStatus === "pulling") {
      void this.pollPull();
      this.startPullPoll();
    }
  };

  private async init() {
    this.loading = true;
    this.error = "";
    try {
      const s = await getSettings();
      // The user may have navigated away while the fetch was in flight;
      // starting pollers now would leak intervals on the orphaned element.
      if (!this.isConnected) return;
      this.settings = s;
      this.seedLimits(s.limits);
      // A pull left running (server boot, another tab) resumes its watch here.
      if (s.image.pullStatus === "pulling") this.startPullPoll();
      void this.refreshContainers();
      void this.loadOverQuota();
      void this.loadReserved();
      this.startPoll();
    } catch (e) {
      this.error = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  private async loadOverQuota() {
    try {
      this.overQuota = await listOverQuotaRepositories();
      this.overQuotaError = "";
    } catch (e) {
      this.overQuotaError = humanError(e);
    }
  }

  private async loadReserved() {
    try {
      this.reserved = await listReservedSecrets();
      this.reservedError = "";
    } catch (e) {
      this.reservedError = humanError(e);
    }
  }

  // ---- Containers polling ----------------------------------------------------

  private startPoll() {
    if (this.pollTimer === undefined && !document.hidden) {
      this.pollTimer = window.setInterval(() => void this.refreshContainers(), POLL_MS);
    }
  }

  private stopPoll() {
    if (this.pollTimer !== undefined) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private async refreshContainers() {
    const id = ++this.containersReq;
    try {
      const report = await listContainers();
      if (id !== this.containersReq) return;
      this.report = report;
      this.substrateDown = false;
      this.containersError = "";
    } catch (e) {
      if (id !== this.containersReq) return;
      // Both outcomes stop the poll: 503 means no runtime answers (no manager
      // is wired, or the orchestrator could not be asked), anything else
      // would just re-fail every 5s. The notice's Retry restarts it.
      if (substrateUnavailable(e)) {
        this.substrateDown = true;
        this.report = undefined;
      } else {
        this.containersError = humanError(e);
      }
      this.stopPoll();
    }
  }

  private retryContainers() {
    this.substrateDown = false;
    this.containersError = "";
    void this.refreshContainers();
    this.startPoll();
  }

  // ---- Image pin ---------------------------------------------------------------

  private async submitPin() {
    const tag = this.tagInput.trim();
    if (tag === "" || this.pinBusy) return;
    this.pinBusy = true;
    try {
      await pinImage(tag);
      // Mirror the 202 locally so the badge flips at once; the fast poll owns
      // the state from here until the pull resolves.
      if (this.settings) {
        this.settings = {
          ...this.settings,
          image: { ...this.settings.image, pullTag: tag, pullStatus: "pulling", pullError: "" },
        };
      }
      this.tagInput = "";
      this.startPullPoll();
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.pinBusy = false;
    }
  }

  private startPullPoll() {
    if (this.pullTimer === undefined && !document.hidden) {
      this.pullTimer = window.setInterval(() => void this.pollPull(), PULL_POLL_MS);
    }
  }

  private stopPullPoll() {
    if (this.pullTimer !== undefined) {
      window.clearInterval(this.pullTimer);
      this.pullTimer = undefined;
    }
  }

  private async pollPull() {
    const id = ++this.settingsReq;
    try {
      const s = await getSettings();
      if (id !== this.settingsReq) return;
      // Only the image portion is taken live — re-seeding the limits form here
      // would clobber an edit in progress.
      this.settings = this.settings ? { ...this.settings, image: s.image } : s;
      if (s.image.pullStatus !== "pulling") {
        this.stopPullPoll();
        // Success gets a toast; failure shows inline via pullError below.
        if (s.image.pullStatus === "ready") {
          toast(`Image ${s.image.tag} pulled and pinned`, "success");
        }
      }
    } catch {
      // Transient poll failure: keep watching, the next tick retries.
    }
  }

  // ---- Global limits -----------------------------------------------------------

  private seedLimits(l: GlobalLimits) {
    this.cpuStr = String(l.cpuCores);
    this.memStr = String(bytesToMib(l.memoryBytes));
    this.pidsStr = String(l.pidsLimit);
    this.idleStr = String(secondsToMinutes(l.idleTimeoutSeconds));
    this.wallStr = String(secondsToMinutes(l.wallClockTimeoutSeconds));
    this.maxStr = String(l.maxConcurrent);
    this.liveFloorStr = String(secondsToMinutes(l.liveIdleTimeoutFloorSeconds));
    this.liveOnStr = String(l.liveMaxAlwaysOn);
    this.branchesStr = String(l.repositoryMaxBranches ?? 0);
    this.storageStr = String(l.storageQuotaBytes ?? 0);
  }

  /**
   * parsedLimits converts the form to wire units, or null when a field is
   * invalid. The six container limits must be > 0; the two Live Artefact
   * governors accept 0 as well, where it is the value "no floor" / "unlimited
   * always-on" rather than an unfilled field. The two repository limits work
   * the same way: 0 is the built-in branch cap and no storage quota. All four
   * are always sent — the PUT replaces the whole set, so omitting them would
   * clear an operator's rationing on the next unrelated save.
   */
  private parsedLimits(): GlobalLimits | null {
    const nums = [this.cpuStr, this.memStr, this.pidsStr, this.idleStr, this.wallStr, this.maxStr].map(
      Number,
    );
    if (!nums.every((n) => Number.isFinite(n) && n > 0)) return null;
    const live = [this.liveFloorStr, this.liveOnStr, this.branchesStr, this.storageStr].map(Number);
    if (!live.every((n) => Number.isFinite(n) && n >= 0)) return null;
    const [cpu, mem, pids, idle, wall, max] = nums;
    const [liveFloor, liveOn, branches, storage] = live;
    return {
      cpuCores: cpu,
      memoryBytes: mibToBytes(mem),
      pidsLimit: Math.round(pids),
      idleTimeoutSeconds: minutesToSeconds(idle),
      wallClockTimeoutSeconds: minutesToSeconds(wall),
      maxConcurrent: Math.round(max),
      liveIdleTimeoutFloorSeconds: minutesToSeconds(liveFloor),
      liveMaxAlwaysOn: Math.round(liveOn),
      repositoryMaxBranches: Math.round(branches),
      storageQuotaBytes: Math.round(storage),
    };
  }

  private async saveLimits() {
    const limits = this.parsedLimits();
    if (!limits || this.saveBusy) return;
    this.saveBusy = true;
    try {
      await updateLimits(limits);
      // Re-fetch for the authoritative state (the PUT body is uncontracted).
      const s = await getSettings();
      this.settings = s;
      this.seedLimits(s.limits);
      toast("Limits saved. Container limits apply to new containers only.", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.saveBusy = false;
    }
  }

  // ---- Sweep + kill ------------------------------------------------------------

  private async sweepNow() {
    if (this.sweepBusy) return;
    this.sweepBusy = true;
    try {
      const rep = await runSweep();
      if (this.report) this.report = { ...this.report, lastSweep: rep };
      toast(`Sweep done: ${rep.stopped ?? 0} stopped, ${rep.orphansKilled} removed`, "success");
      void this.refreshContainers();
    } catch (e) {
      if (substrateUnavailable(e)) this.substrateDown = true;
      else toast(humanError(e), "error");
    } finally {
      this.sweepBusy = false;
    }
  }

  private async confirmKill() {
    const target = this.killTarget;
    if (!target || this.killing) return;
    this.killing = true;
    try {
      await killContainer(target.id);
      toast("Container killed", "success");
      this.killTarget = null;
      void this.refreshContainers();
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.killing = false;
    }
  }

  // ---- Render ------------------------------------------------------------------

  private renderKpis() {
    const r = this.report;
    const sweep = r?.lastSweep;
    const tiles: Array<{ label: string; value: string }> = [
      { label: "Running", value: r ? `${r.running}/${r.maxConcurrent}` : "—" },
      { label: "Orphans removed", value: sweep ? String(sweep.orphansKilled) : "—" },
      { label: "Stale builds at boot", value: r ? String(r.bootRecovery.staleBuildsMarked) : "—" },
      { label: "Adopted at boot", value: r ? String(r.bootRecovery.containersAdopted) : "—" },
    ];
    return html`
      <div>
        <div class="kpis">
          ${tiles.map(
            (t) => html`<div class="tile">
              <div class="eyebrow">${t.label}</div>
              <div class="num">${t.value}</div>
            </div>`,
          )}
        </div>
        <div class="sweep-row">
          <span class="sweep-caption">
            ${sweep ? `Last sweep ${relTime(sweep.finishedAt)}` : "No sweep has run yet."}
          </span>
          <bn-button
            variant="ghost"
            size="sm"
            ?loading=${this.sweepBusy}
            ?disabled=${this.substrateDown}
            @click=${() => void this.sweepNow()}
            >Run sweep now</bn-button
          >
        </div>
        ${sweep && sweep.errors.length > 0
          ? html`<bn-panel class="sweep-errors" eyebrow="Sweep errors" style="margin-top:var(--bino-space-md)">
              ${sweep.errors.map((err) => html`<p>${err}</p>`)}
            </bn-panel>`
          : nothing}
      </div>
    `;
  }

  private renderImageCard() {
    const img = this.settings!.image;
    const digestShort = img.digest ? img.digest.slice(0, 19) + "…" : "—";
    return html`<bn-card heading="Image pin">
      ${img.versionError
        ? html`<p class="pin-error version-error" role="alert">
            Renders, builds, lint, previews and live reports are paused: ${img.versionError}.
            Always-on live reports start again on their next visit once this is fixed.
          </p>`
        : nothing}
      <div class="image-meta">
        <div>
          <div class="k">Current tag</div>
          <div class="v" title=${`${img.repo}:${img.tag}`}>${img.tag || "—"}</div>
        </div>
        <div>
          <div class="k">Digest</div>
          <div class="v" title=${img.digest}>${digestShort}</div>
        </div>
        <div>
          <div class="k">bino version</div>
          <div class="v">${img.version || "—"}</div>
        </div>
        <div>
          <div class="k">Pull status</div>
          <div>
            <bn-badge .variant=${pullVariant(img.pullStatus)} ?dot=${img.pullStatus === "pulling"}
              >${img.pullStatus}${img.pullStatus === "pulling" && img.pullTag ? ` ${img.pullTag}` : ""}</bn-badge
            >
          </div>
        </div>
      </div>
      <bn-field
        label="Pin a new version"
        hint="A release tag (${img.minVersion} or newer) or digest (sha256:…). A digest is checked with bino version after the pull. The change applies to new containers only."
      >
        <div class="pin-row">
          <bn-input
            mono
            placeholder=${img.minVersion}
            .value=${this.tagInput}
            ?disabled=${img.pullStatus === "pulling"}
            @input=${(e: CustomEvent) => (this.tagInput = (e.detail as { value: string }).value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void this.submitPin();
              }
            }}
          ></bn-input>
          <bn-button
            variant="primary"
            ?loading=${this.pinBusy || img.pullStatus === "pulling"}
            ?disabled=${this.tagInput.trim() === ""}
            @click=${() => void this.submitPin()}
            >Validate &amp; pull</bn-button
          >
        </div>
      </bn-field>
      ${img.pullStatus === "error" && img.pullError
        ? html`<p class="pin-error" role="alert">Pull of ${img.pullTag || "the last tag"} failed: ${img.pullError}</p>`
        : nothing}
    </bn-card>`;
  }

  private renderBackupCard() {
    const b = this.settings!.backup;
    const state = backupState(b);
    return html`<bn-card heading="Database backup">
      ${state === "failing"
        ? html`<p class="pin-error version-error" role="alert">
            The last run failed at the ${b.lastFailureStep} step
            <span title=${b.lastFailureAt ?? ""}>${relTime(b.lastFailureAt ?? "")}</span>:
            ${b.lastFailureError}
          </p>`
        : nothing}
      <div class="image-meta backup-meta">
        <div>
          <div class="k">Status</div>
          <div><bn-badge .variant=${backupVariant(state)}>${state}</bn-badge></div>
        </div>
        <div>
          <div class="k">Schedule</div>
          <div class="v">${b.schedule ? `${b.schedule} (UTC)` : "off"}</div>
        </div>
        <div>
          <div class="k">Last success</div>
          <div class="v" title=${b.lastSuccessAt ?? ""}>${relTime(b.lastSuccessAt ?? "")}</div>
        </div>
        <div>
          <div class="k">File</div>
          <div class="v" title=${b.lastSuccessName}>${b.lastSuccessName || "—"}</div>
        </div>
        <div>
          <div class="k">Size</div>
          <div class="v">
            ${b.lastSuccessAt ? `${bytesToMib(b.lastSuccessBytes).toFixed(1)} MiB` : "—"}
          </div>
        </div>
      </div>
    </bn-card>`;
  }

  private limitField(
    label: string,
    hint: string,
    value: string,
    set: (v: string) => void,
    step = "1",
  ) {
    return html`<bn-field label=${label} hint=${hint} required>
      <input
        type="number"
        step=${step}
        min="0"
        aria-label=${label}
        .value=${value}
        @input=${(e: Event) => set((e.target as HTMLInputElement).value)}
      />
    </bn-field>`;
  }

  private renderLimitsCard() {
    const valid = this.parsedLimits() !== null;
    return html`<bn-card heading="Global limits">
      <div class="limits-grid">
        ${this.limitField("CPU cores", "Per container.", this.cpuStr, (v) => (this.cpuStr = v), "0.25")}
        ${this.limitField("Memory (MiB)", "Per container.", this.memStr, (v) => (this.memStr = v))}
        ${this.limitField("Max processes", "pids limit per container.", this.pidsStr, (v) => (this.pidsStr = v))}
        ${this.limitField("Idle timeout (min)", "Previews idle longer are stopped.", this.idleStr, (v) => (this.idleStr = v))}
        ${this.limitField("Max lifetime (min)", "Hard wall-clock cap per container.", this.wallStr, (v) => (this.wallStr = v))}
        ${this.limitField("Max concurrent", "Containers across all teams.", this.maxStr, (v) => (this.maxStr = v))}
        ${this.limitField("Live idle floor (min)", "Shortest scale-to-zero delay a live report may use; 0 = none.", this.liveFloorStr, (v) => (this.liveFloorStr = v))}
        ${this.limitField("Always-on live", "Always-on live reports per team; 0 = unlimited.", this.liveOnStr, (v) => (this.liveOnStr = v))}
        ${this.limitField("Branch workspaces per repository (0 = 10)", "Branches of one repository that get a workspace.", this.branchesStr, (v) => (this.branchesStr = v))}
        ${this.limitField("Storage quota bytes (0 = unlimited)", "Per team. Git storage and data (if the data area is on) count separately.", this.storageStr, (v) => (this.storageStr = v))}
      </div>
      <div class="save-row">
        <bn-button
          variant="primary"
          ?loading=${this.saveBusy}
          ?disabled=${!valid}
          @click=${() => void this.saveLimits()}
          >Save limits</bn-button
        >
      </div>
    </bn-card>`;
  }

  /**
   * The repositories whose default branch is above the size, file or
   * directory limit. Their reads and builds work, but until they are below
   * the limits again only changes to the default branch that do not make it
   * larger are accepted, plus deletes of other branches, so the team may need
   * a word from the operator. What the repository takes on disk is shown
   * too; it is not what the limits hold.
   */
  private renderOverQuotaCard() {
    return html`<bn-card heading="Repositories over their size or file limit" flush>
      <bn-button slot="actions" variant="ghost" size="sm" @click=${() => void this.loadOverQuota()}
        >Refresh</bn-button
      >
      ${this.overQuotaError
        ? html`<div class="notice"><span>${this.overQuotaError}</span></div>`
        : html`<bn-table
            .columns=${[
              { key: "name", label: "Repository" },
              { key: "team", label: "Team", mono: true },
              { key: "origin", label: "Origin", width: "1px" },
              { key: "size", label: "On disk", align: "right", mono: true, width: "1px" },
            ] as BnColumn[]}
            .rows=${this.overQuota.map(
              (r): BnRow => ({
                name: r.name,
                team: r.teamId,
                origin: r.origin,
                size: `${Math.round(bytesToMib(r.sizeBytes))} MiB`,
              }),
            )}
            empty="No repository is over its size or file limit."
          ></bn-table>`}
    </bn-card>`;
  }

  /**
   * The secrets whose names the cluster reserves for itself, across all
   * teams. The teams that own them see them marked on their secret lists and
   * can delete them; values are never shown.
   */
  private renderReservedCard() {
    return html`<bn-card heading="Reserved secret names" flush>
      <bn-button slot="actions" variant="ghost" size="sm" @click=${() => void this.loadReserved()}
        >Refresh</bn-button
      >
      ${this.reservedError
        ? html`<div class="notice"><span>${this.reservedError}</span></div>`
        : html`<bn-table
            .columns=${[
              { key: "name", label: "Name", mono: true },
              { key: "scope", label: "Scope", width: "1px" },
              { key: "owner", label: "Owner", mono: true },
              { key: "team", label: "Team", mono: true },
            ] as BnColumn[]}
            .rows=${this.reserved.map(
              (s): BnRow => ({ name: s.name, scope: s.scope, owner: s.ownerId, team: s.teamId }),
            )}
            empty="No secret uses a reserved name."
          ></bn-table>`}
    </bn-card>`;
  }

  private containerRows(items: ContainerInfo[]): BnRow[] {
    return items.map((c) => ({
      id: html`<span title=${c.id}>${c.id.slice(0, 12)}</span>`,
      kind: html`<bn-badge variant=${c.kind === "preview" ? "info" : "neutral"}>${c.kind}</bn-badge>`,
      team: c.teamName || c.teamId || "—",
      workspace: c.workspaceName || c.workspaceId || "—",
      image: html`<span title=${c.image}>${imageTag(c.image)}</span>`,
      started: relTime(c.startedAt),
      cpu: c.cpuPercent < 0 ? "—" : `${c.cpuPercent.toFixed(0)}%`,
      memory: c.memoryBytes < 0 ? "—" : `${Math.round(bytesToMib(c.memoryBytes))} MiB`,
      pids: c.pids < 0 ? "—" : String(c.pids),
      actions: html`<bn-button
        variant="danger"
        size="sm"
        @click=${() => (this.killTarget = c)}
        aria-label=${"Kill container " + c.id}
        >Kill</bn-button
      >`,
    }));
  }

  private renderContainersCard() {
    if (this.substrateDown) {
      return html`<bn-card heading="Containers">
        <div class="notice">
          <span>
            Container administration is unavailable. The sandbox runtime did not answer.
          </span>
          <bn-button variant="secondary" size="sm" @click=${() => this.retryContainers()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    if (this.containersError) {
      return html`<bn-card heading="Containers">
        <div class="notice">
          <span>${this.containersError}</span>
          <bn-button variant="secondary" size="sm" @click=${() => this.retryContainers()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    const r = this.report;
    if (!r) {
      return html`<bn-card heading="Containers"
        ><bn-spinner block label="Loading containers…"></bn-spinner
      ></bn-card>`;
    }
    if (r.items.length === 0) {
      return html`<bn-card heading="Containers" flush>
        <bn-empty-state
          icon="▣"
          heading="No containers running"
          message="Tenant builds, renders, and previews appear here while they run."
        ></bn-empty-state>
      </bn-card>`;
    }
    const columns: BnColumn[] = [
      { key: "id", label: "ID", mono: true },
      { key: "kind", label: "Kind" },
      { key: "team", label: "Team" },
      { key: "workspace", label: "Workspace" },
      { key: "image", label: "Image", mono: true },
      { key: "started", label: "Started" },
      { key: "cpu", label: "CPU", align: "right", mono: true },
      { key: "memory", label: "Memory", align: "right", mono: true },
      { key: "pids", label: "PIDs", align: "right", mono: true },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    return html`<bn-card heading="Containers" flush>
      <bn-table .columns=${columns} .rows=${this.containerRows(r.items)}></bn-table>
    </bn-card>`;
  }

  private renderKillModal() {
    const target = this.killTarget;
    return html`<bn-modal
      ?open=${target !== null}
      heading="Kill container?"
      @close=${() => {
        if (!this.killing) this.killTarget = null;
      }}
    >
      ${target
        ? html`<p>
            Container <span class="confirm-name">${target.id.slice(0, 12)}</span> (${target.kind}${target.teamName ? `, ${target.teamName}` : ""})
            will be killed immediately and its operation will fail.
          </p>`
        : nothing}
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => {
          if (!this.killing) this.killTarget = null;
        }}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.killing}
        @click=${() => void this.confirmKill()}
        >Kill container</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    if (this.loading && !this.settings) {
      return html`<bn-spinner block label="Loading runtime settings…"></bn-spinner>`;
    }
    if (this.error || !this.settings) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn't load runtime settings"
        message=${this.error || "No settings available."}
      >
        <bn-button slot="actions" variant="primary" @click=${() => void this.init()}>Retry</bn-button>
      </bn-empty-state>`;
    }
    return html`
      <div class="head">
        <h1>Admin</h1>
      </div>
      ${adminTabs("runtime")}
      <div class="sections">
        ${this.renderKpis()} ${this.renderImageCard()} ${this.renderBackupCard()}
        ${this.renderLimitsCard()} ${this.renderOverQuotaCard()} ${this.renderReservedCard()}
        ${this.renderContainersCard()}
      </div>
      ${this.renderKillModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-admin": PageAdmin;
  }
}
